import { useState, useEffect, useCallback } from 'react';
import { providerApi } from '../../contexts/ProviderAuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Upload, Image as ImageIcon, Trash2, Star, Loader2, 
  CheckCircle, AlertCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_ICONS = {
  draft: AlertCircle,
  submitted: Clock,
  approved: CheckCircle
};

export function MediaUploader() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const fetchMedia = useCallback(async () => {
    try {
      const data = await providerApi('/provider/media');
      setMedia(data.media || []);
    } catch (err) {
      console.error('Failed to fetch media:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    
    for (const file of files) {
      try {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name}: Solo immagini sono supportate`);
          continue;
        }
        
        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: File troppo grande (max 10MB)`);
          continue;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('is_cover', media.length === 0 ? 'true' : 'false');
        
        const token = localStorage.getItem('provider_token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/provider/media/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || 'Upload failed');
        }
        
        const result = await res.json();
        setMedia(prev => [...prev, result]);
        toast.success(`${file.name} caricato!`);
      } catch (err) {
        toast.error(`${file.name}: ${err.message}`);
      }
    }
    
    setUploading(false);
    fetchMedia(); // Refresh to get full metadata
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa immagine?')) return;
    
    try {
      await providerApi(`/provider/media/${mediaId}`, { method: 'DELETE' });
      setMedia(prev => prev.filter(m => m.id !== mediaId));
      toast.success('Immagine eliminata');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleUpload(Array.from(e.target.files));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-umbria-olive" />
      </div>
    );
  }

  return (
    <Card data-testid="media-uploader">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          Immagini
        </CardTitle>
        <CardDescription>
          Carica le foto della tua attività. La prima immagine sarà usata come copertina.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Upload Zone */}
        <div
          className={`
            border-2 border-dashed rounded-xl p-8 text-center transition-colors
            ${dragActive ? 'border-umbria-olive bg-umbria-olive/5' : 'border-gray-200 hover:border-umbria-olive/50'}
            ${uploading ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input').click()}
        >
          <input
            id="file-input"
            type="file"
            className="hidden"
            accept="image/*"
            multiple
            onChange={handleFileInput}
            disabled={uploading}
            data-testid="media-file-input"
          />
          
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-10 w-10 animate-spin text-umbria-olive" />
              <p className="text-umbria-muted">Caricamento in corso...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload className="h-10 w-10 text-umbria-olive/60" />
              <p className="font-medium text-umbria-charcoal">
                Trascina qui le immagini o clicca per selezionarle
              </p>
              <p className="text-sm text-umbria-muted">
                JPG, PNG, WebP - Max 10MB per file
              </p>
            </div>
          )}
        </div>

        {/* Media Grid */}
        {media.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {media.map((item, index) => {
              const StatusIcon = STATUS_ICONS[item.content_status] || AlertCircle;
              
              return (
                <div
                  key={item.id}
                  className="relative group rounded-lg overflow-hidden border border-gray-200 aspect-square"
                  data-testid={`media-item-${index}`}
                >
                  <img
                    src={item.url.startsWith('http') ? item.url : `${process.env.REACT_APP_BACKEND_URL}${item.url}`}
                    alt={item.alt_text || item.original_filename}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                      data-testid={`delete-media-${index}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Badges */}
                  <div className="absolute top-2 left-2 flex gap-1">
                    {item.is_cover && (
                      <Badge className="bg-amber-500 text-white">
                        <Star className="h-3 w-3 mr-1" />
                        Cover
                      </Badge>
                    )}
                  </div>
                  
                  {/* Status indicator */}
                  <div className="absolute bottom-2 right-2">
                    <StatusIcon className={`h-4 w-4 ${
                      item.content_status === 'approved' ? 'text-green-500' :
                      item.content_status === 'submitted' ? 'text-amber-500' :
                      'text-gray-400'
                    }`} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {media.length === 0 && !uploading && (
          <div className="text-center py-8 text-umbria-muted">
            <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-30" />
            <p>Nessuna immagine caricata</p>
            <p className="text-sm">Aggiungi almeno una foto della tua attività</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
